import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { actions } from "@/app/handlers/enums.js";
import { isValidEmail, stripHTML } from "@/app/handlers/validationFunctions.js";
import useDSPProfileState from "@/app/hooks/use-DSPProfileState";
import useScrollIntoView from "@/app/hooks/useScrollIntoView";
import { supportedJobs, useFeatureFlag } from "@/app/hooks/useFeatureFlag.jsx";
import { useSmartProfile } from "@/app/services/create-resume/queries.js";

import { ApplicationDetails } from "../utils/helpers";

interface UseJobApplicationReturn {
	showDSPModal: boolean;
	setShowDSPModal: React.Dispatch<React.SetStateAction<boolean>>;
	showApplyModal: boolean;
	setShowApplyModal: React.Dispatch<React.SetStateAction<boolean>>;
	scrollToEmail: boolean;
	setScrollToEmail: React.Dispatch<React.SetStateAction<boolean>>;
	applicationRef: React.RefObject<HTMLDivElement>;
	onApply: (applicationLink: string | null) => void;
	handleDSPModalSelect: (option: unknown) => void;
	dspModalData: unknown;
	isOneClickApplyEnabled: boolean;
}

/**
 * Hook to manage job application flow, modals, and related state
 * @param applicationDetails - The extracted application details
 * @param setApplicationDetails - Setter for application details
 * @param isUserLoggedIn - Whether user is logged in
 * @param jobId - The job ID
 * @returns Application flow state and handlers
 */
export function useJobApplication(
	applicationDetails: ApplicationDetails,
	setApplicationDetails: React.Dispatch<React.SetStateAction<ApplicationDetails>>,
	isUserLoggedIn: boolean,
	jobId: string,
): UseJobApplicationReturn {
	const navigate = useNavigate();
	const [showDSPModal, setShowDSPModal] = useState(false);
	const [showApplyModal, setShowApplyModal] = useState(false);
	const [scrollToEmail, setScrollToEmail] = useState(false);
	const applicationRef = useScrollIntoView(scrollToEmail);

	const dspProfileState = useDSPProfileState();
	const dspModalData = dspProfileState.dspModalData(actions.CREATE);
	const { data: smartProfileData } = useSmartProfile();

	const oneClickApply = useFeatureFlag("oneClickApply");

	const isOneClickApplyEnabled = useMemo(() => {
		if (supportedJobs.includes(jobId)) {
			return true;
		}
		return oneClickApply;
	}, [oneClickApply, jobId]);

	const onApply = (applicationLink: string | null) => {
		if (applicationLink) {
			const link = stripHTML(applicationLink).replace(/\W+$/, "");

			if (isValidEmail(link)) {
				const referer = document.location.pathname + document.location.search;
				// Check if user is logged in
				if (!isUserLoggedIn) {
					navigate("/signin?ref=" + referer);
					return;
				}

				// Check if the user has a DSP
				const hasDSPData = Object.keys(smartProfileData || {}).length > 0;

				if (hasDSPData) {
					if (!isOneClickApplyEnabled) {
						setScrollToEmail(!scrollToEmail);
					} else {
						setShowApplyModal(true);
					}
				}

				if (!hasDSPData) {
					setShowDSPModal(true);
				}
			}

			if (!isValidEmail(link)) {
				setApplicationDetails({ link, applicationType: "URL", jobId: null, companyId: null });
				setShowApplyModal(true);
			}
		}
	};

	const handleDSPModalSelect = (option: unknown) => {
		dspProfileState.onDSPSelect(option);
		setShowDSPModal(false);
	};

	return {
		showDSPModal,
		setShowDSPModal,
		showApplyModal,
		setShowApplyModal,
		scrollToEmail,
		setScrollToEmail,
		applicationRef,
		onApply,
		handleDSPModalSelect,
		dspModalData,
		isOneClickApplyEnabled,
	};
}
